import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, KeyRound, MapPin, Navigation, X } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StationModule, { Frame } from "@/components/StationModules";
import ImageComparison from "@/components/ImageComparison";
import CollectibleCard, { collectibleFromWalk } from "@/components/CollectibleCard";

const guestProgressKey = (walkId) => `spuren_guest_progress:${walkId}`;

function emptyGuestProgress() {
  return {
    status: "IN_PROGRESS",
    completed_stations: [],
    answers: {},
    codes_found: {},
    path_choices: {},
    final_thought: null,
  };
}


export default function WalkPlayer() {
  const { walkId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("station");
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  const [finale, setFinale] = useState(null);
  const [finalThought, setFinalThought] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [cardTaken, setCardTaken] = useState(false);

  useEffect(() => {
    api.get(`/walks/${walkId}`).then(({ data: payload }) => setData(payload)).catch(() => setError("Dieser Weg ist nicht verfügbar."));
  }, [walkId]);

  useEffect(() => {
    if (loading || !data?.walk?.id) return;

    const applyProgress = (p) => {
      setProgress(p);
      const stations = data.stations || [];
      const done = p.completed_stations || [];
      const next = stations.findIndex((s) => !done.includes(s.id));
      const resolvedIndex = next === -1 ? Math.max(stations.length - 1, 0) : next;
      setIndex(resolvedIndex);

      if (p.status === "COMPLETED") {
        setIndex(Math.max(stations.length - 1, 0));
        setPhase("finale");
        setFinale({
          ok: true,
          guest: !user,
          returned: true,
          granted_achievements: [],
          walk_title: data.walk.title,
          cognitive_space: null,
        });
      }
    };

    if (user) {
      api
        .post(`/progress/${data.walk.id}/start`)
        .then(({ data: p }) => applyProgress(p))
        .catch(() => setProgress(null));
      return;
    }

    try {
      const raw = localStorage.getItem(guestProgressKey(data.walk.id));
      const p = raw ? { ...emptyGuestProgress(), ...JSON.parse(raw) } : emptyGuestProgress();
      localStorage.setItem(guestProgressKey(data.walk.id), JSON.stringify(p));
      applyProgress(p);
    } catch {
      const p = emptyGuestProgress();
      setProgress(p);
      applyProgress(p);
    }
  }, [user, loading, data]);

  const stations = useMemo(() => data?.stations || [], [data?.stations]);
  const station = stations[index];
  const totalFragments = Math.max(stations.length - 1, 1);
  const doneCount = (progress?.completed_stations || []).length;

  const segment = useMemo(() => {
    if (!station || !data) return null;
    const nextStation = stations[index + 1];
    if (!nextStation) return null;
    const candidates = (data.segments || []).filter(
      (s) => s.from_station_id === station.id && s.to_station_id === nextStation.id
    );
    const chosen = (progress?.path_choices || {})[station.id];
    return candidates.find((s) => s.variant_key && s.variant_key === chosen) || candidates.find((s) => !s.variant_key) || candidates[0] || null;
  }, [station, data, stations, index, progress]);

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;
  if (error) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">{error}</div>;
  if (!data || !progress || !station) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;

  const key = (moduleKey) => `${station.id}:${moduleKey}`;
  const answers = progress.answers || {};
  const codesFound = progress.codes_found || {};
  const guestMode = !user;

  const updateGuestProgress = (updater) => {
    setProgress((current) => {
      const base = current || emptyGuestProgress();
      const next = typeof updater === "function" ? updater(base) : { ...base, ...updater };
      try {
        localStorage.setItem(guestProgressKey(data.walk.id), JSON.stringify(next));
      } catch {
        // Local storage can be unavailable in strict private modes; the current session still works.
      }
      return next;
    });
  };

  const saveAnswer = async (moduleKey, value) => {
    if (guestMode) {
      updateGuestProgress((current) => ({
        ...current,
        answers: { ...(current.answers || {}), [key(moduleKey)]: value },
      }));
      return;
    }
    try {
      const { data: p } = await api.post(`/progress/${data.walk.id}/answer`, {
        station_id: station.id,
        module_key: moduleKey,
        value,
      });
      setProgress(p);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const choosePath = async (variantKey) => {
    if (guestMode) {
      updateGuestProgress((current) => ({
        ...current,
        path_choices: { ...(current.path_choices || {}), [station.id]: variantKey },
      }));
      return;
    }
    try {
      const { data: p } = await api.post(`/progress/${data.walk.id}/choose-path`, {
        station_id: station.id,
        variant_key: variantKey,
      });
      setProgress(p);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const canContinue = (station.modules || []).every((module) => {
    if (module.type === "COMMUNITY_FRAGE") return answers[key(module.key)] != null;
    if (module.type === "WEGWAHL") return (progress.path_choices || {})[station.id] != null;
    if (module.type === "CODE_SUCHE" || module.type === "FUNDSTUECK") {
      const found = codesFound[key(module.key)] || [];
      return found.length >= (module.code_count || 0);
    }
    return true;
  });

  const completeStation = async () => {
    if (guestMode) {
      updateGuestProgress((current) => {
        const done = [...(current.completed_stations || [])];
        if (!done.includes(station.id)) done.push(station.id);
        return { ...current, completed_stations: done };
      });
      if (index + 1 < stations.length) setPhase("segment");
      else setPhase("finale");
      return;
    }

    try {
      const { data: p } = await api.post(`/progress/${data.walk.id}/complete-station`, { station_id: station.id });
      setProgress(p);
      if (index + 1 < stations.length) {
        setPhase("segment");
      } else {
        setPhase("finale");
      }
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const finishWalk = async (thought = null) => {
    setFinishing(true);
    setError("");
    try {
      const { data: result } = await api.post(`/progress/${data.walk.id}/complete`, { final_thought: thought || null });
      if (guestMode) {
        const completedGuest = {
          ...(progress || emptyGuestProgress()),
          status: "COMPLETED",
          completed_at: new Date().toISOString(),
          final_thought: thought || null,
        };
        try {
          localStorage.setItem(guestProgressKey(data.walk.id), JSON.stringify(completedGuest));
        } catch {
          // Session completion still works if persistent browser storage is unavailable.
        }
        setProgress(completedGuest);
      }
      setFinale(result);
      const cognitive = result?.cognitive_space;
      if (thought && cognitive?.ok) {
        const returnTo = `${window.location.origin}/wege/${data.walk.id}/los?returned=1`;
        const params = new URLSearchParams({ return: returnTo, source: "spuren" });
        if (cognitive.cluster_id) params.set("pulse", cognitive.cluster_id);
        window.location.assign(`https://space.artpart.at/space/st-ruprecht?${params.toString()}`);
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setFinishing(false);
    }
  };

  const header = (
    <header className="sticky top-0 z-30 border-b bg-[rgba(248,245,238,0.92)] backdrop-blur-xl">
      <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
        <button className="btn btn-ghost !p-2" onClick={() => navigate(`/wege/${walkId}`)} data-testid="player-exit" aria-label="beenden">
          <X className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold">{data.walk.title}</p>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--brand-soft)]">
            <div className="h-full rounded-full transition-all" style={{ width: `${(doneCount / stations.length) * 100}%`, background: "var(--brand)" }} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {guestMode ? (
            <span className="rounded-full bg-[var(--brand-soft)] px-2 py-1 text-[10px] font-bold tracking-wide text-[var(--brand)]">
              GAST
            </span>
          ) : null}
          <span className="text-xs text-[var(--ink-soft)]" data-testid="player-station-count">
            {Math.min(index + 1, stations.length)}/{stations.length}
          </span>
        </div>
      </div>
    </header>
  );

  if (phase === "segment" && segment) {
    const nextStation = stations[index + 1];
    return (
      <div data-testid="segment-screen" className="min-h-screen bg-[#eef1e7]">
        {header}
        <div className="mx-auto max-w-2xl px-5 py-8">
          <div className="rounded-[2rem] border border-[#d8ddcf] bg-[#f5f7f0] p-5 shadow-sm sm:p-7">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#dfe8d8] text-[var(--brand)]">
                <Navigation className="h-5 w-5" />
              </span>
              <div>
                <p className="eyebrow !text-[var(--brand)]">Wegbeschreibung zur nächsten Station</p>
                <h1 className="mt-1 text-3xl sm:text-4xl">{segment.title || "Weiter"}</h1>
                <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                  Du hast Station {index + 1} abgeschlossen. Jetzt zählt nur der Weg bis zum nächsten Punkt.
                </p>
              </div>
            </div>

            {segment.route_image ? (
              <div className="mt-6">
                <div
                  className="h-[62vh] min-h-[420px] max-h-[680px] overflow-auto overscroll-contain rounded-3xl border bg-white"
                  style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
                  data-testid="segment-route-map"
                >
                  <img
                    src={fileUrl(segment.route_image)}
                    alt="Wegkarte zur nächsten Station"
                    className="block h-auto w-full max-w-none"
                    draggable="false"
                    data-testid="segment-route-image"
                  />
                </div>
                <p className="mt-2 text-center text-xs text-[var(--ink-soft)]">
                  Karte mit dem Finger verschieben.
                </p>
              </div>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-[#c9d0c1] bg-white/70 p-5 text-sm text-[var(--ink-soft)]">
                Die Wegkarte wird hier ergänzt. Die Beschreibung darunter führt dich bereits zum nächsten Punkt.
              </div>
            )}

            {segment.instruction ? (
              <p className="mt-5 whitespace-pre-line text-base leading-relaxed">{segment.instruction}</p>
            ) : null}

            <div className="mt-5 flex flex-wrap gap-3 text-sm">
              {segment.distance_text ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2" data-testid="segment-distance">
                  <MapPin className="h-4 w-4 text-[var(--brand)]" /> {segment.distance_text}
                </span>
              ) : null}
              {segment.duration_text ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2" data-testid="segment-duration">
                  <Clock className="h-4 w-4 text-[var(--brand)]" /> {segment.duration_text}
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-5 rounded-3xl border bg-white p-5">
            <p className="eyebrow">Nächste Station</p>
            <div className="mt-2 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl">{nextStation?.title || "Nächster Punkt"}</h2>
                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                  {nextStation?.location_description || "Dort beginnt die nächste Station."}
                </p>
              </div>
              {segment.destination_image ? (
                <img
                  src={fileUrl(segment.destination_image)}
                  alt=""
                  className="h-24 w-28 shrink-0 rounded-2xl border object-cover"
                  data-testid="segment-destination-image"
                />
              ) : null}
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink-soft)]">
              Erst wenn du dort bist, öffnest du die nächste Station.
            </p>
          </div>

          <button
            className="btn btn-primary mt-5 w-full !py-4"
            onClick={() => {
              setIndex(index + 1);
              setPhase("station");
              window.scrollTo({ top: 0 });
            }}
            data-testid="segment-continue"
          >
            ICH BIN DORT <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === "finale" || finale) {
    const cognitive = finale?.cognitive_space;
    const completionReward = finale?.completion_reward;
    const digitalCard = completionReward?.collectible || collectibleFromWalk(data?.walk);
    const xpPreview = completionReward?.xp_awarded || completionReward?.xp_preview || 0;
    const physicalCardAvailable = Boolean(
      digitalCard?.physical_available ?? data?.walk?.collectible_physical_enabled
    );
    return (
      <div data-testid="finale-screen">
        {header}
        <div className="mx-auto max-w-2xl px-5 py-10">
          <p className="eyebrow">{totalFragments} / {totalFragments} Fragmente</p>
          <h1 className="mt-2 text-4xl">Du bist zurück.</h1>
          {station?.hero_image ? (
            <img src={fileUrl(station.hero_image)} alt="" className="mt-5 w-full rounded-3xl border object-cover" />
          ) : null}
          {finale ? (
            <div className="mt-6 rounded-3xl border bg-white p-6" data-testid="finale-complete">
              <p className="text-2xl">SPUREN abgeschlossen</p>
              {cognitive?.ok ? (
                <p className="mt-3 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="cognitive-space-ok">
                  {cognitive.message || "Dein Gedanke ist jetzt Teil des Gedankenraums St. Ruprecht."}
                </p>
              ) : finalThought.trim() ? (
                <p className="mt-3 rounded-xl border px-4 py-3 text-sm" data-testid="cognitive-space-fallback">
                  Dein Weg ist abgeschlossen. Der Gedanke wurde gespeichert, konnte aber gerade nicht an den Cognitive Space übertragen werden.
                </p>
              ) : null}
              <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)]">
                {physicalCardAvailable
                  ? "Diese Spur gibt es doppelt: digital in deinem Profil und als echte Sammelkarte im Kasten."
                  : "Die Sammelkarte dieser Spur wird digital in deinem Profil gespeichert."}
                {guestMode ? " Als Gast bleibt dieser Abschluss nur auf diesem Gerät gespeichert." : ""}
              </p>
              {digitalCard ? (
                <section className="mt-6 rounded-3xl bg-[var(--brand-soft)] p-5" data-testid="finale-collectible">
                  <p className="eyebrow">Deine digitale Sammelkarte</p>
                  <h2 className="mb-5 mt-1 text-2xl">
                    {completionReward?.newly_collected ? "Neu in deiner Sammlung." : "Diese Spur gehört zu dir."}
                  </h2>
                  <CollectibleCard value={digitalCard} testId="finale-collectible-card" />
                  {xpPreview > 0 ? (
                    <p className="mt-5 text-center text-sm font-bold text-[var(--brand)]" data-testid="finale-xp-awarded">+{xpPreview} SPUREN-XP</p>
                  ) : completionReward?.saved && completionReward?.xp_balance != null ? (
                    <p className="mt-5 text-center text-sm font-bold text-[var(--brand)]" data-testid="finale-xp-balance">{completionReward.xp_balance} SPUREN-XP gesammelt</p>
                  ) : null}
                  {guestMode ? (
                    <p className="mt-3 text-center text-xs leading-relaxed text-[var(--ink-soft)]">Lege ein Konto an, damit Karte und XP dauerhaft in deinem Profil bleiben.</p>
                  ) : null}
                </section>
              ) : null}
              {(finale.granted_achievements || []).length ? (
                <div className="mt-4 space-y-2">
                  {finale.granted_achievements.map((a) => (
                    <p key={a.id} className="rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid={`achievement-${a.achievement_id}`}>
                      Erfolg erhalten: <strong>{a.title}</strong>
                    </p>
                  ))}
                </div>
              ) : null}
              <div className="mt-5 flex flex-wrap gap-2">
                {guestMode ? (
                  <>
                    <Link to="/registrieren" className="btn btn-primary !py-2.5 !text-xs">KONTO ANLEGEN & KÜNFTIG SAMMELN</Link>
                    <Link to="/login" className="btn btn-ghost !py-2.5 !text-xs">ANMELDEN</Link>
                  </>
                ) : (
                  <Link to="/profil" className="btn btn-primary !py-2.5 !text-xs" data-testid="finale-to-profile">ZU MEINER SAMMLUNG</Link>
                )}
                <Link to="/wege" className="btn btn-ghost !py-2.5 !text-xs">WEITERE WEGE</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-5 rounded-3xl border bg-white p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)]">
                    <KeyRound className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="eyebrow">Der Kasten</p>
                    <h2 className="mt-1 text-2xl">Öffnen. Eine Karte nehmen. Eine Spur mitnehmen.</h2>
                    <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                      Öffne den realen Kasten mit dem Schlüssel vor Ort und nimm genau eine SPUREN-Sammelkarte heraus.
                    </p>
                  </div>
                </div>
                <button
                  className={`btn mt-5 w-full ${cardTaken ? "btn-ghost" : "btn-primary"}`}
                  onClick={() => setCardTaken(true)}
                  data-testid="finale-card-taken"
                >
                  {cardTaken ? "KARTE GEHOLT ✓" : "ICH HABE EINE KARTE"}
                </button>
              </div>

              <div className={`mt-5 rounded-3xl border bg-white p-5 transition-opacity ${cardTaken ? "opacity-100" : "opacity-50"}`}>
                <p className="eyebrow">Gedankenraum</p>
                <h2 className="mt-1 text-2xl">Welcher Gedanke ist geblieben?</h2>
                <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                  Wenn du möchtest, gib ihn dem Gedankenraum St. Ruprecht. Der Eintrag ist freiwillig.
                </p>
                <textarea
                  className="field mt-4 min-h-28 resize-y"
                  value={finalThought}
                  disabled={!cardTaken}
                  onChange={(e) => setFinalThought(e.target.value.slice(0, 500))}
                  placeholder="Ein Fragment, ein Gedanke, ein Gefühl …"
                  data-testid="finale-thought"
                />
                <p className="mt-1 text-right text-xs text-[var(--ink-soft)]">{finalThought.length}/500</p>
              </div>
              {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}
              <div className="mt-5 space-y-3">
                <button
                  className="btn btn-primary w-full"
                  onClick={() => finishWalk(finalThought.trim())}
                  disabled={!cardTaken || !finalThought.trim() || finishing}
                  data-testid="finale-send-thought"
                >
                  {finishing ? "WIRD ÜBERGEBEN …" : "GEDANKEN IN DEN RAUM GEBEN"}
                </button>
                <button
                  className="btn btn-ghost w-full"
                  onClick={() => finishWalk(null)}
                  disabled={!cardTaken || finishing}
                  data-testid="finale-without-thought"
                >
                  OHNE GEDANKEN ABSCHLIESSEN
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="station-screen">
      {header}
      <div className="mx-auto max-w-2xl px-5 py-7">
        <p className="eyebrow">Station {index + 1}{station.subtitle ? ` · ${station.subtitle}` : ""}</p>
        <h1 className="mt-1 text-4xl" data-testid="station-title">{station.title}</h1>
        {station.location_description ? (
          <p className="mt-2 inline-flex items-start gap-2 text-sm text-[var(--ink-soft)]" data-testid="station-location">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {station.location_description}
          </p>
        ) : null}
        {station.comparison_before_image && (station.comparison_after_image || station.hero_image) ? (
          <div className="mt-5">
            <p className="eyebrow">Ein Ort · Zwei Zeiten</p>
            <h2 className="mt-1 text-3xl">Schieb die Vergangenheit ins Heute.</h2>
            <div className="mt-4">
              <ImageComparison
                before={station.comparison_before_image}
                after={station.comparison_after_image || station.hero_image}
                beforeLabel={station.comparison_before_label || "Damals"}
                afterLabel={station.comparison_after_label || "Heute"}
                caption={station.comparison_caption}
              />
            </div>
          </div>
        ) : station.hero_image ? (
          <img src={fileUrl(station.hero_image)} alt="" className="mt-5 w-full rounded-3xl border object-cover" />
        ) : null}
        {station.story_text ? <p className="mt-5 whitespace-pre-line text-base leading-relaxed">{station.story_text}</p> : null}
        {station.task_text ? (
          <p className="mt-3 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm">{station.task_text}</p>
        ) : null}

        <div className="mt-6 space-y-4">
          {(station.modules || []).map((module) => (
            <StationModule
              key={module.key}
              module={module}
              walkId={data.walk.id}
              stationId={station.id}
              discovery={data.discovery_items || []}
              contributors={data.contributors || []}
              answer={answers[key(module.key)]}
              onAnswer={(value) => saveAnswer(module.key, value)}
              pathChoice={(progress.path_choices || {})[station.id]}
              onChoosePath={choosePath}
              found={codesFound[key(module.key)] || []}
              total={module.code_count || 0}
              onFound={(found, total) => {
                const updater = (current) => ({
                  ...current,
                  codes_found: { ...(current.codes_found || {}), [key(module.key)]: found },
                });
                if (guestMode) updateGuestProgress(updater);
                else setProgress(updater);
              }}
              photo={photo}
              onPhoto={setPhoto}
              onLeft={() => {}}
              guestMode={guestMode}
              fragmentLabel={`${Math.min(index + 1, totalFragments)} von ${totalFragments} Fragmenten`}
            />
          ))}
        </div>

        {error ? <p className="mt-4 text-sm text-red-700" data-testid="player-error">{error}</p> : null}

        {!canContinue ? (
          <p className="mt-6 text-sm text-[var(--ink-soft)]" data-testid="station-blocked">
            Erledige die Aufgabe dieser Station, dann geht es weiter.
          </p>
        ) : null}
        <div className="mt-6 border-t pt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink-soft)]">Station</p>
          <button className="btn btn-primary mt-3 w-full !py-4" onClick={completeStation} disabled={!canContinue} data-testid="station-continue">
            STATION ABSCHLIESSEN <CheckCircle2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
